import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getQuotations, updateQuotationStatus, createQuotation, getProducts, getCustomers } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { FileText, CheckCircle, XCircle, Clock, Plus, Search, Trash2 } from 'lucide-react';

const Quotations = () => {
  const { user } = useAuth();
  const [quotations, setQuotations] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [quotationsData, productsData, customersData] = await Promise.all([
        getQuotations(),
        getProducts(),
        getCustomers()
      ]);
      setQuotations(quotationsData);
      setProducts(productsData);
      setCustomers(customersData);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await updateQuotationStatus(id, status);
      toast.success('Estado actualizado');
      loadData();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const addToCart = (product, size, color) => {
    const variant = product.stock_variants.find(
      v => v.size === size && v.color === color
    );

    if (!variant || variant.stock <= 0) {
      toast.error('Producto sin stock');
      return;
    }

    const existingItem = cart.find(
      item => item.product_id === product.id && item.size === size && item.color === color
    );

    if (existingItem) {
      updateQuantity(existingItem, existingItem.quantity + 1);
    } else {
      const newItem = {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        size: size,
        color: color,
        discount: 0,
        subtotal: product.price
      };
      setCart([...cart, newItem]);
      toast.success(`${product.name} agregado`);
    }
  };

  const updateQuantity = (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item);
      return;
    }

    const updatedCart = cart.map(cartItem => {
      if (cartItem === item) {
        return {
          ...cartItem,
          quantity: newQuantity,
          subtotal: cartItem.unit_price * newQuantity
        };
      }
      return cartItem;
    });
    setCart(updatedCart);
  };

  const removeFromCart = (item) => {
    setCart(cart.filter(cartItem => cartItem !== item));
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
    const itbis = subtotal * 0.18;
    const total = subtotal + itbis;
    return { subtotal, itbis, total };
  };

  const handleCreateQuotation = async () => {
    if (cart.length === 0) {
      toast.error('Debe agregar productos a la cotización');
      return;
    }

    if (!selectedCustomer) {
      toast.error('Debe seleccionar un cliente');
      return;
    }

    if (!validUntil) {
      toast.error('Debe especificar la fecha de validez');
      return;
    }

    try {
      const quotationData = {
        customer_id: selectedCustomer.id,
        customer_name: `${selectedCustomer.name} ${selectedCustomer.last_name}`,
        items: cart,
        valid_until: validUntil,
        notes: notes
      };

      await createQuotation(quotationData, user.id);
      toast.success('Cotización creada exitosamente');
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cotización');
    }
  };

  const resetForm = () => {
    setCart([]);
    setSelectedCustomer(null);
    setValidUntil('');
    setNotes('');
    setSearchTerm('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pendiente: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      convertida: { label: 'Convertida', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      rechazada: { label: 'Rechazada', className: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.pendiente;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const { subtotal, itbis, total } = calculateTotals();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">Cargando...</div>;
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="quotations-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit' }}>Cotizaciones</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={resetForm}
              className="bg-cyan-500 hover:bg-cyan-600 text-white shadow-md shadow-cyan-500/20"
              data-testid="add-quotation-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Nueva Cotización
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Nueva Cotización</DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Products Section */}
              <div className="space-y-4">
                <div>
                  <Label>Buscar Productos</Label>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar productos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="quotation-search-input"
                    />
                  </div>
                </div>

                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {filteredProducts.map(product => (
                    <Card key={product.id} className="border border-slate-200">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-semibold">{product.name}</p>
                            <p className="text-sm text-slate-500">{product.category}</p>
                          </div>
                          <p className="font-bold text-cyan-600 mono-font">${product.price.toFixed(2)}</p>
                        </div>
                        <div className="space-y-1">
                          {product.stock_variants.map((variant, idx) => (
                            <Button
                              key={idx}
                              onClick={() => addToCart(product, variant.size, variant.color)}
                              disabled={variant.stock <= 0}
                              className="w-full text-xs bg-slate-900 hover:bg-slate-800"
                              size="sm"
                            >
                              {variant.size} - {variant.color} ({variant.stock})
                            </Button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Cart Section */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <Select onValueChange={(value) => {
                    const customer = customers.find(c => c.id === value);
                    setSelectedCustomer(customer);
                  }}>
                    <SelectTrigger data-testid="quotation-customer-select">
                      <SelectValue placeholder="Seleccionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map(customer => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name} {customer.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Válida Hasta *</Label>
                  <Input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    data-testid="quotation-valid-until-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Input
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas adicionales..."
                    data-testid="quotation-notes-input"
                  />
                </div>

                <div>
                  <Label className="mb-2 block">Productos ({cart.length})</Label>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {cart.map((item, index) => (
                      <div key={index} className="bg-slate-50 p-2 rounded border border-slate-200">
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.product_name}</p>
                            <p className="text-xs text-slate-500">{item.size} - {item.color}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFromCart(item)}
                            className="h-6 w-6 text-rose-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-2">
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => updateQuantity(item, item.quantity - 1)}
                              className="h-6 w-6"
                            >
                              -
                            </Button>
                            <span className="w-8 text-center font-bold mono-font">{item.quantity}</span>
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => updateQuantity(item, item.quantity + 1)}
                              className="h-6 w-6"
                            >
                              +
                            </Button>
                          </div>
                          <span className="font-bold mono-font">${item.subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span className="mono-font font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>ITBIS (18%):</span>
                    <span className="mono-font font-medium">${itbis.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t">
                    <span>TOTAL:</span>
                    <span className="mono-font text-cyan-600">${total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateQuotation}
                    className="flex-1 bg-cyan-500 hover:bg-cyan-600"
                    data-testid="create-quotation-button"
                  >
                    Crear Cotización
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    data-testid="cancel-quotation-button"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4" data-testid="quotations-list">
        {quotations.map(quotation => (
          <Card key={quotation.id} className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`quotation-card-${quotation.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center" style={{ fontFamily: 'Outfit' }}>
                    <FileText className="w-5 h-5 mr-2 text-cyan-500" />
                    {quotation.quotation_number}
                  </CardTitle>
                  <p className="text-sm text-slate-500 mt-1">{quotation.customer_name}</p>
                </div>
                {getStatusBadge(quotation.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Subtotal</p>
                  <p className="font-bold mono-font">${quotation.subtotal.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">ITBIS</p>
                  <p className="font-bold mono-font">${quotation.itbis.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Total</p>
                  <p className="font-bold text-cyan-600 mono-font">${quotation.total.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Válida hasta</p>
                  <p className="font-medium">{quotation.valid_until}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm font-semibold mb-2">Items:</p>
                <div className="space-y-2">
                  {quotation.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm bg-slate-50 p-2 rounded">
                      <span>{item.product_name} ({item.quantity}x)</span>
                      <span className="font-bold mono-font">${item.subtotal.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {quotation.status === 'pendiente' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={() => handleStatusChange(quotation.id, 'convertida')}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                    data-testid={`convert-quotation-${quotation.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Convertir a Factura
                  </Button>
                  <Button
                    onClick={() => handleStatusChange(quotation.id, 'rechazada')}
                    variant="outline"
                    className="text-rose-500 hover:bg-rose-50"
                    data-testid={`reject-quotation-${quotation.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {quotations.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            No hay cotizaciones registradas
          </div>
        )}
      </div>
    </div>
  );
};

export default Quotations;
